<script setup lang="ts">
    import {
        IonApp,
        IonRouterOutlet,
        actionSheetController,
        alertController,
    } from "@ionic/vue";
    import { URLOpenListenerEvent } from "@capacitor/app";
    import { Capacitor } from "@capacitor/core";
    import router from "@/router";
    import { computed, onMounted, ref, watch } from "vue";
    import { App } from "@capacitor/app";
    import {
        ActionPerformed,
        PushNotifications,
    } from "@capacitor/push-notifications";
    import { store } from "@/store";
    import StorageService from "./services/storage.service";
    import moment from "@/utils/moment";
    import i18n, { supportedLocales, type SupportedLocale } from "@/i18n";
    import { useI18n } from "vue-i18n";
    import { LiveUpdate } from "@capawesome/capacitor-live-update";
    import { SplashScreen } from "@capacitor/splash-screen";
    import { useRoute } from "vue-router";
    import { StatusBar, Style } from "@capacitor/status-bar";
    import { Device } from "@capacitor/device";
    import axios from "axios";
    import ApiService from "@/services/api.service";
    import useTheme from "@/composables/useTheme";
    import { useConsent } from "@/composables/useConsent";
    import CookieConsentBanner from "@/components/CookieConsentBanner.vue";
    import AppPromotionBanner from "@/components/AppPromotionBanner.vue";
    import ConnectivityBanner from "@/components/ConnectivityBanner.vue";
    import AuroraStage from "@/components/animated/AuroraStage.vue";
    import { useConnectivity } from "@/composables/useConnectivity";
    import { usePredictionQueue } from "@/composables/usePredictionQueue";

    moment.locale(i18n.global.locale.value);
    const { t } = useI18n();
    const user = computed(() => store.getters["profile/user"]);
    const route = useRoute();
    const { initializeTheme, effectiveTheme } = useTheme();
    const {
        consentStatus,
        isLoaded: consentLoaded,
        syncConsentFromApi,
    } = useConsent();
    const isNativePlatform = Capacitor.isNativePlatform();
    const PENDING_UPDATE_KEY = "pendingUpdateApply";
    const updatePromptVisible = ref(false);

    const hideNativeSplashImmediately = async () => {
        try {
            await SplashScreen.hide();
        } catch {
            // ignore
        }
    };

    const withTimeout = async <T,>(
        promise: Promise<T>,
        timeoutMs: number,
        timeoutMessage: string,
    ): Promise<T> => {
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutHandle = setTimeout(() => {
                reject(new Error(timeoutMessage));
            }, timeoutMs);
        });

        try {
            return await Promise.race([promise, timeoutPromise]);
        } finally {
            if (timeoutHandle) {
                clearTimeout(timeoutHandle);
            }
        }
    };

    App.addListener("appUrlOpen", function (event: URLOpenListenerEvent) {
        const slug = getSlug(event.url);

        if (slug) {
            router.push("/" + getSlug(slug));
        }
    });

    App.addListener("resume", async function () {
        if (!isNativePlatform) {
            return;
        }

        try {
            const pending = await StorageService.pull(PENDING_UPDATE_KEY);
            if (pending) {
                await reloadAfterUpdate();
                return;
            }
            await updateApp();
        } catch (error) {
            console.error("Failed to update app on resume:", error);
        }
    });

    const reloadAfterUpdate = async (): Promise<void> => {
        await LiveUpdate.reload();
    };

    const promptApplyUpdate = async (): Promise<void> => {
        if (updatePromptVisible.value) {
            return;
        }
        updatePromptVisible.value = true;

        try {
            const alert = await alertController.create({
                header: t("update.available.title"),
                message: t("update.available.message"),
                backdropDismiss: false,
                buttons: [
                    {
                        text: t("update.available.later"),
                        role: "cancel",
                        handler: async () => {
                            await StorageService.set(PENDING_UPDATE_KEY, "1");
                        },
                    },
                    {
                        text: t("update.available.apply-now"),
                        handler: reloadAfterUpdate,
                    },
                ],
            });
            await alert.present();
            await alert.onDidDismiss();
        } finally {
            updatePromptVisible.value = false;
        }
    };

    const updateApp = async () => {
        try {
            const rawEnvValue = import.meta.env.VUE_APP_LIVE_UPDATES_ENABLED;
            const liveUpdatesEnabled =
                String(rawEnvValue).toLowerCase() !== "false";
            const currentBundle = (await LiveUpdate.getCurrentBundle())
                .bundleId;

            if (!liveUpdatesEnabled) {
                await LiveUpdate.reset();
                await updateDeviceId();
                return;
            }
            await updateDeviceId();

            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 10000);
            const versions = await (
                await fetch(
                    `https://app.sportspoule.com/versions.json?t=${Date.now()}`,
                    { signal: controller.signal },
                )
            ).json();
            clearTimeout(timeout);

            const channel = (await StorageService.get("channel")) ?? "stable";
            const channelData = versions.channels[channel];
            const latestBundleId = channelData.bundleId;

            const compatible =
                compareVersions(
                    channelData.compatibleVersion,
                    (await App.getInfo()).version,
                ) < 1;
            if (currentBundle === latestBundleId || !compatible) {
                return;
            }

            const cached = (await LiveUpdate.getBundles()).bundleIds;
            if (!cached.includes(latestBundleId)) {
                await withTimeout(
                    LiveUpdate.downloadBundle({
                        bundleId: latestBundleId,
                        url:
                            "https://app.sportspoule.com/bundles/" +
                            latestBundleId +
                            ".zip",
                        ...(channelData.checksum && {
                            checksum: channelData.checksum,
                        }),
                    }),
                    45000,
                    "Download bundle timeout",
                );
            }

            await LiveUpdate.setNextBundle({ bundleId: latestBundleId });
            await promptApplyUpdate();
        } catch (error) {
            console.error("Failed to update app:", error);
        }
    };

    const compareVersions = (a: string, b: string) => {
        const pa = a.split(".");
        const pb = b.split(".");
        for (let i = 0; i < 3; i++) {
            const na = Number(pa[i]);
            const nb = Number(pb[i]);
            if (na > nb) return 1;
            if (nb > na) return -1;
            if (!isNaN(na) && isNaN(nb)) return 1;
            if (isNaN(na) && !isNaN(nb)) return -1;
        }
        return 0;
    };

    function getSlug(url: string) {
        const baseUrl = import.meta.env.VUE_APP_BASE_URL;
        const replaceItems: string[] = [
            import.meta.env.VUE_APP_BASE_URL + "/",
            ...(baseUrl ? [baseUrl] : []),
            "app.sportspoule.com/",
            "app.sportspoule.com",
            "http://",
            "https://",
            "sportspoule:///",
            "sportspoule://",
        ];

        replaceItems.forEach((item) => {
            url = url.replace(item, "");
        });

        return url;
    }

    const isPushNotificationsAvailable =
        Capacitor.isPluginAvailable("PushNotifications");

    watch(
        user,
        async (newUser) => {
            if (Object.keys(newUser).length > 0) {
                if (
                    newUser.locale &&
                    supportedLocales.includes(
                        newUser.locale as SupportedLocale,
                    ) &&
                    i18n.global.locale.value !== newUser.locale
                ) {
                    i18n.global.locale.value = newUser.locale;
                    localStorage.setItem("app_locale", newUser.locale);
                    moment.locale(newUser.locale);
                    axios.defaults.headers.common["Accept-Language"] =
                        newUser.locale;
                }
                if (isPushNotificationsAvailable) {
                    await checkPermissions();
                }
                await updateDeviceId();
                syncConsentFromApi();
            }
        },
        {
            immediate: true,
        },
    );

    async function updateDeviceId() {
        // Only update device ID if user is logged in
        if (Object.keys(user.value).length === 0) {
            return;
        }
        const id = (await Device.getId()).identifier;
        return ApiService.post(`/profile/device-id`, {
            device_id: id,
        }).catch((error: any) => {
            if (error?.response?.status === 401) {
                return;
            }
            throw error;
        });
    }

    async function registerFcmToken(token: any) {
        if (Object.keys(user.value).length === 0) {
            return;
        }
        if (!token.value || !token.value.trim()) {
            return;
        }
        try {
            const deviceId = (await Device.getId()).identifier;
            await ApiService.post("/profile/fcm-token", {
                fcm_token: token.value,
                device_id: deviceId,
            });
        } catch (error: any) {
            if (error?.response?.status === 401) {
                return;
            }
            console.error("Failed to register FCM token:", error);
        }
    }

    watch(
        () => route.path,
        (path) => {
            if (!Capacitor.isNativePlatform()) {
                return;
            }
            if (path.startsWith("/profile")) {
                StatusBar.setStyle({
                    style: Style.Light,
                });
            } else {
                if (effectiveTheme.value !== "dark") {
                    StatusBar.setStyle({
                        style: Style.Dark,
                    });
                }
            }
        },
        {
            immediate: true,
        },
    );

    async function checkPermissions() {
        if ((await StorageService.get("pushNotifications")) != "false") {
            if (
                (await PushNotifications.checkPermissions()).receive ==
                "granted"
            ) {
                initPushNotifications();
            } else {
                (
                    await actionSheetController.create({
                        header: "Do you want to enable push notifications?",
                        buttons: [
                            {
                                text: "Yes",
                                handler: () => {
                                    initPushNotifications();
                                },
                            },
                            {
                                text: "No",
                                role: "cancel",
                                handler: () => {
                                    StorageService.set(
                                        "pushNotifications",
                                        "false",
                                    );
                                },
                            },
                        ],
                    })
                ).present();
            }
        }
    }

    function initPushNotifications() {
        PushNotifications.requestPermissions().then(({ receive }) => {
            if (receive === "granted") {
                // Register with Apple / Google to receive push via APNS/FCM
                PushNotifications.register();
                PushNotifications.addListener("registration", (token: any) => {
                    registerFcmToken(token);
                });

                PushNotifications.addListener(
                    "pushNotificationActionPerformed",
                    (action: ActionPerformed) => {
                        const data = action.notification.data ?? {};
                        if (data.tracking_template) {
                            ApiService.post("t", {
                                type: `push.${data.tracking_template}.cta_clicked`,
                                properties: {
                                    url: data.url ?? null,
                                    send_id: data.tracking_send_id ?? null,
                                },
                            }).catch(() => {
                                // tracking is best-effort
                            });
                        }
                        if (data.url) {
                            const slug = getSlug(data.url);
                            if (slug) {
                                router.push("/" + getSlug(slug));
                            }
                        }
                    },
                );
            } else {
                // Show some error
                alert("error");
            }
        });
    }
    onMounted(async () => {
        // Connectivity tracking + offline prediction queue (web + native).
        useConnectivity().init();
        void usePredictionQueue().init();

        if (isNativePlatform) {
            await hideNativeSplashImmediately();
            try {
                await LiveUpdate.ready();
            } catch (error) {
                console.error("LiveUpdate.ready failed:", error);
            }
        }

        await initializeTheme();

        if (!isNativePlatform) {
            return;
        }

        try {
            await updateApp();
        } catch (error) {
            console.error("Failed during app initialization:", error);
        }
    });
</script>

<template>
    <ion-app>
        <AuroraStage />
        <ion-router-outlet />
        <CookieConsentBanner v-if="consentLoaded && consentStatus === null" />
        <AppPromotionBanner v-if="!isNativePlatform" />
        <ConnectivityBanner />
    </ion-app>
</template>
